import React, { useMemo, useState } from "react";
import { fromDisplayWeight, shownWeight, shownWeightUnit, showWeight } from "../../../utils/units";
import { Activity, LineChart } from "lucide-react";
import DraggableStepper from "../../ui/draggable-stepper";
import HelpLink from "../../help/HelpLink";
import { Field, ItemRow, SummaryRow, Tag } from "../ProfileParts";
import { benchmarkDetail, benchmarkFigure } from "../profileSummaries";
import { clock, isErg } from "../../../utils/benchmarkNormalise";
import {
  amountParts,
  benchmarkGroups,
  changeFigure,
  describeGroup,
  effortPoints,
  formatAmount,
  liftCurve,
  progressSeries,
  workCurve,
} from "../../../utils/capabilityCurves";
import { LiftCurveChart, LiftHistoryChart, ProgressChart, WorkCurveChart } from "./CurveCharts";

/**
 * Your curves (issue #91): pick an exercise and see what your results add up
 * to. A lift shows the weight for any number of reps, with an "on the bar"
 * calculator; a machine shows pace (or calories a minute) against how long
 * you go, with a calculator inside the span of your efforts; anything else
 * (max reps, a hold, watts) shows each result over time.
 */

const round25 = (kg) => Math.round(kg / 2.5) * 2.5;
const KIND_TITLES = { lift: "Lifts", distance: "Distance", calories: "Calories", progress: "Progress" };

const Stat = ({ label, value, unit, lead = false }) => (
  <div className="grid min-w-0 gap-0.5">
    <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
      {lead && <span className="inline-block h-0.5 w-2.5 bg-primary" aria-hidden="true" />}
      {label}
    </span>
    <span className="text-2xl font-semibold leading-none tabular-nums text-foreground">{value}</span>
    <span className="text-xs text-muted-foreground">{unit}</span>
  </div>
);

const Readout = ({ children }) => <div className="grid grid-cols-3 gap-2">{children}</div>;

const Note = ({ children }) => <p className="text-xs leading-snug text-muted-foreground">{children}</p>;

const Answer = ({ children }) => (
  <p className="text-sm leading-snug text-muted-foreground" aria-live="polite">
    {children}
  </p>
);

const Calculator = ({ children }) => (
  <div className="grid gap-2 rounded-xl border border-border bg-muted/20 p-3">{children}</div>
);

// ─── Lifts ───────────────────────────────────────────────

const LiftCalculator = ({ curve, bar, setBar }) => {
  const at = curve.repsAt(bar);
  let answer;
  if (at.over) answer = <>{`That's above your 1RM of `}<b className="text-foreground">{showWeight(curve.oneRm)}</b>.</>;
  else if (at.many)
    answer = (
      <>
        <b className="text-foreground">More than 20 reps.</b> That far out it's an endurance set, and the
        estimate isn't reliable.
      </>
    );
  else {
    const n = Math.floor(at.reps);
    answer = (
      <>
        About <b className="text-foreground">{`${n} rep${n === 1 ? "" : "s"}`}</b> to failure.
        {n > 2 && (
          <>
            {" "}For a hard set with 2 in reserve, aim for <b className="text-foreground">{n - 2}</b>.
          </>
        )}
      </>
    );
  }
  return (
    <Calculator>
      {shownWeightUnit() === "lb" ? (
        <DraggableStepper
          label="On the bar"
          value={shownWeight(bar)}
          onChange={(lb) => setBar(fromDisplayWeight(lb, "lb"))}
          min={5}
          max={shownWeight(curve.oneRm * 1.2)}
          step={5}
          unit="lb"
        />
      ) : (
        <DraggableStepper
          label="On the bar"
          value={bar}
          onChange={setBar}
          min={2.5}
          max={round25(curve.oneRm * 1.2)}
          step={2.5}
          unit="kg"
        />
      )}
      <Answer>{answer}</Answer>
    </Calculator>
  );
};

const leanSentence = (k) =>
  k < 0.95
    ? "You hold on for more reps than average."
    : k > 1.05
      ? "You drop off faster than average."
      : "You're close to average.";

const LiftView = ({ rows, now }) => {
  const curve = useMemo(() => liftCurve(rows, now), [rows, now]);
  const [bar, setBar] = useState(() => (curve ? round25(curve.kgAt(10)) : 20));
  if (!curve) return <Note>Add the reps you did with this weight to see a curve.</Note>;
  const fitted = curve.curve;
  return (
    <>
      <Readout>
        <Stat lead label="1RM" value={shownWeight(curve.oneRm)} unit={`${shownWeightUnit()}, ${fitted ? "fitted" : "combined"}`} />
        <Stat label="5RM" value={shownWeight(round25(curve.kgAt(5)))} unit={shownWeightUnit()} />
        <Stat label="10RM" value={shownWeight(round25(curve.kgAt(10)))} unit={shownWeightUnit()} />
      </Readout>
      <LiftCurveChart curve={curve} bar={bar} />
      <Note>
        {fitted
          ? `Fitted to your tested max and ${fitted.sets} hard set${fitted.sets === 1 ? "" : "s"}. ${leanSentence(curve.k)}`
          : "Follows the standard chart. Log a tested max and a hard set of 3 or more reps to fit it to you."}
        {curve.conflict &&
          ` Your results disagree (${shownWeight(curve.conflict.low)}–${showWeight(curve.conflict.high)}), so treat this as rough.`}
      </Note>
      <LiftCalculator curve={curve} bar={bar} setBar={setBar} />
      {curve.entries.length > 1 && (
        <Field label="Where the 1RM comes from">
          <LiftHistoryChart curve={curve} now={now} />
          <Note>Bigger dots count for more. Hollow dots are rough estimates from sets well short of failure.</Note>
        </Field>
      )}
    </>
  );
};

// ─── Machines ────────────────────────────────────────────

const LADDER = [500, 1000, 2000, 3000, 5000, 6000, 10000, 21097];

const DistanceCalculator = ({ curve, per }) => {
  const lo = curve.amountIn(curve.span[0]);
  const hi = curve.amountIn(curve.span[1]);
  const mid = Math.sqrt(lo * hi);
  const [metres, setMetres] = useState(() => {
    const inside = LADDER.filter((d) => d >= lo && d <= hi);
    if (!inside.length) return Math.round(mid / 100) * 100;
    return inside.reduce((a, b) => (Math.abs(Math.log(b / mid)) < Math.abs(Math.log(a / mid)) ? b : a));
  });
  const sec = curve.timeFor(metres);
  return (
    <Calculator>
      <DraggableStepper label="Distance" value={metres} onChange={setMetres} min={100} max={42200} step={100} unit="m" />
      <Answer>
        {sec != null ? (
          <>
            About <b className="text-foreground">{clock(sec)}</b>
            {`, ${clock((sec / metres) * per)} /${per === 500 ? "500 m" : "km"}.`}
          </>
        ) : (
          `Outside your efforts (${Math.round(lo / 100) * 100}–${Math.round(hi / 100) * 100} m). Log a ${
            metres < lo ? "shorter" : "longer"
          } one to cover it.`
        )}
      </Answer>
    </Calculator>
  );
};

const CaloriesCalculator = ({ curve }) => {
  const [minutes, setMinutes] = useState(() =>
    Math.min(30, Math.max(2, Math.round(Math.sqrt(curve.span[0] * curve.span[1]) / 60))),
  );
  const cals = curve.amountIn(minutes * 60);
  return (
    <Calculator>
      <DraggableStepper label="For" value={minutes} onChange={setMinutes} min={1} max={60} step={1} unit="min" />
      <Answer>
        {cals != null ? (
          <>
            About <b className="text-foreground">{`${Math.round(cals)} calories`}</b>
            {`, ${Math.round((cals / minutes) * 10) / 10} a minute.`}
          </>
        ) : (
          `Outside your efforts (${clock(curve.span[0])}–${clock(curve.span[1])}). Log a ${
            minutes * 60 < curve.span[0] ? "shorter" : "longer"
          } one to cover it.`
        )}
      </Answer>
    </Calculator>
  );
};

const WorkView = ({ group, now }) => {
  const curve = useMemo(() => workCurve(group.rows, now), [group, now]);
  const points = useMemo(() => effortPoints(group.rows, now), [group, now]);
  const calories = group.kind === "calories";
  const per = calories ? null : group.rows.some((r) => isErg(r.label)) ? 500 : 1000;
  const unit = calories ? "cal/min" : `/${per === 500 ? "500 m" : "km"}`;
  const rate = (r) => (calories ? `${Math.round(r * 600) / 10}` : clock(per / r));

  return (
    <>
      {curve ? (
        <Readout>
          <Stat lead label="Threshold" value={rate(curve.cs)} unit={unit} />
          <Stat
            label="Above it"
            value={calories ? `${Math.round(curve.reserve)}` : `${Math.round(curve.reserve)} m`}
            unit={calories ? "cal in reserve" : "in reserve"}
          />
          <Stat label="Sprint" value={curve.sprintRate != null ? rate(curve.sprintRate) : "–"} unit={`${unit} best`} />
        </Readout>
      ) : (
        <Note>
          A curve needs two efforts of 2 to 30 minutes, one at least half as long again as the other, at Hard
          or All out.
        </Note>
      )}
      {points.length > 0 && <WorkCurveChart curve={curve} points={points} per={per} />}
      <Note>
        The shaded bands show which energy system mostly powers an effort of that length. They're a rough guide
        based on time, not a measurement of you.
      </Note>
      {curve && (calories ? <CaloriesCalculator curve={curve} /> : <DistanceCalculator curve={curve} per={per} />)}
    </>
  );
};

// ─── Anything else: progress over time ───────────────────

const ProgressView = ({ rows, now }) => {
  const series = useMemo(() => progressSeries(rows, now), [rows, now]);
  if (!series) return <Note>Add a date to your results to see them over time.</Note>;
  const format = (v) => formatAmount(v, series.unit);
  const latest = amountParts(series.latest.value, series.unit);
  const best = amountParts(series.best.value, series.unit);
  const since = series.first.date.toLocaleDateString("en-AU", { month: "short", year: "numeric" });
  return (
    <>
      <Readout>
        <Stat lead label="Latest" value={latest.value} unit={latest.unit || "time"} />
        <Stat label="Best" value={best.value} unit={best.unit || "time"} />
        <Stat
          label="Change"
          value={changeFigure(series.change, series.unit)}
          unit={series.better == null ? `since ${since}` : `${series.better ? "better" : "worse"} since ${since}`}
        />
      </Readout>
      <ProgressChart series={series} format={format} now={now} />
      <Note>
        {series.unit === "sec"
          ? series.higher
            ? "A hold, so longer is better."
            : "A time for the job, so faster is better and sits higher on the chart."
          : "Higher is better."}{" "}
        Hollow dots are results where you held back.
      </Note>
    </>
  );
};

// ─── The view and its picker ─────────────────────────────

/** Every exercise with a curve shape, grouped by kind, to choose from. */
export const CurvePicker = ({ caps, selected, onSelect }) => {
  const now = useMemo(() => new Date(), []);
  const groups = benchmarkGroups(caps);
  return Object.keys(KIND_TITLES).map((kind) => {
    const items = groups.filter((g) => g.kind === kind);
    if (!items.length) return null;
    return (
      <Field key={kind} label={KIND_TITLES[kind]} aside={String(items.length)}>
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
          {items.map((g) => {
            const { figure, detail } = describeGroup(g, now);
            return (
              <ItemRow
                key={g.key}
                Icon={LineChart}
                title={g.label}
                detail={figure || detail}
                tag={g.key === selected && <Tag>Showing</Tag>}
                onClick={() => onSelect(g.key)}
              />
            );
          })}
        </div>
      </Field>
    );
  });
};

const CurvesView = ({ caps, groupKey, onPick, onOpenEntry }) => {
  const now = useMemo(() => new Date(), []);
  const groups = benchmarkGroups(caps);
  const group = groups.find((g) => g.key === groupKey) || groups[0];
  if (!group) return <Note>Log a lift for reps, or a timed effort, to see a curve.</Note>;
  const lift = group.kind === "lift";
  const progress = group.kind === "progress";

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <SummaryRow Icon={LineChart} label="Exercise" value={group.label} onClick={onPick} />
      </div>
      {lift ? (
        <LiftView key={group.key} rows={group.rows} now={now} />
      ) : progress ? (
        <ProgressView key={group.key} rows={group.rows} now={now} />
      ) : (
        <WorkView key={group.key} group={group} now={now} />
      )}
      <Field label="Results" aside={String(group.rows.length)}>
        <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
          {group.rows.map((c) => {
            const figure = benchmarkFigure(c);
            return (
              <ItemRow
                key={c.id}
                Icon={Activity}
                title={c.label}
                detail={benchmarkDetail(c)}
                tag={figure && <Tag>{figure}</Tag>}
                onClick={() => onOpenEntry(c.id)}
              />
            );
          })}
        </div>
      </Field>
      <div className="grid gap-1.5 text-sm leading-snug text-muted-foreground">
        {lift ? (
          <p>
            <b className="font-semibold text-foreground">Your curve</b> shows what you could lift for each number of
            reps. The standard chart is an average, and people differ a lot past 5 reps. A recent tested max plus a
            hard set or two at higher reps lets Khronos fit the curve to you. Past 20 reps a set says more about
            endurance than strength, so the curve stops there.
          </p>
        ) : progress ? (
          <p>
            <b className="font-semibold text-foreground">Progress</b> lines up every result for this exercise by the
            date you did it. Results with a different unit, or done in a different time, show as their own exercise.
          </p>
        ) : (
          <p>
            <b className="font-semibold text-foreground">Threshold</b> is roughly the hardest effort you can hold for a
            long time. It comes from your efforts of 2 to 30 minutes. The curve stops where your efforts stop: Khronos
            doesn't guess past them.
          </p>
        )}
        <HelpLink topic="account-sync#your-curves" label="How your curves are worked out" />
      </div>
    </>
  );
};

export default CurvesView;
